import { EquirectangularReflectionMapping, PMREMGenerator, Texture, WebGLRenderer } from 'three';

interface PendingPmremJob {
	key: string;
	texture: Texture;
	resolve: (envMap: Texture) => void;
	reject: (error: Error) => void;
}

/**
 * PmremPreloader manages pre-generation of PMREM environment maps during idle time.
 * This prevents UI blocking when switching sky textures by doing the expensive
 * PMREMGenerator work before it's needed.
 */
export class PmremPreloader {
	#renderer: WebGLRenderer | null = null;
	#pmremGenerator: PMREMGenerator | null = null;
	#shaderCompiled = false;
	#preloadedEnvMaps = new Map<string, Texture>();
	#pendingJobs: PendingPmremJob[] = [];
	#isProcessing = false;
	#idleCallbackId: number | null = null;

	/**
	 * Initialize the preloader with a WebGL renderer.
	 * Compiles the PMREM shader immediately (one-time cost).
	 */
	initialize(renderer: WebGLRenderer): void {
		this.#renderer = renderer;
		this.#pmremGenerator = new PMREMGenerator(renderer);
		this.#pmremGenerator.compileEquirectangularShader();
		this.#shaderCompiled = true;
	}

	/**
	 * Check if the preloader has been initialized
	 */
	get isInitialized(): boolean {
		return this.#shaderCompiled && this.#pmremGenerator !== null;
	}

	/**
	 * Queue a texture for PMREM pre-generation.
	 * Returns a promise that resolves when the environment map is ready.
	 */
	preload(key: string, texture: Texture): Promise<Texture> {
		// Return cached if already pre-generated
		const cached = this.#preloadedEnvMaps.get(key);
		if (cached) {
			return Promise.resolve(cached);
		}

		// Check if already queued
		const existingJob = this.#pendingJobs.find((job) => job.key === key);
		if (existingJob) {
			return new Promise((resolve, reject) => {
				// Chain onto existing job
				const originalResolve = existingJob.resolve;
				const originalReject = existingJob.reject;
				existingJob.resolve = (envMap: Texture) => {
					originalResolve(envMap);
					resolve(envMap);
				};
				existingJob.reject = (error: Error) => {
					originalReject(error);
					reject(error);
				};
			});
		}

		return new Promise((resolve, reject) => {
			this.#pendingJobs.push({ key, texture, resolve, reject });
			this.scheduleProcessing();
		});
	}

	/**
	 * Synchronously generate a PMREM environment map, reusing the already-initialized
	 * PMREMGenerator. Use this when you need the result immediately (e.g., during
	 * material creation) instead of scheduling idle-time processing.
	 */
	generatePmremSync(key: string, texture: Texture): Texture | null {
		// Return cached if already generated
		const cached = this.#preloadedEnvMaps.get(key);
		if (cached) return cached;

		if (!this.#pmremGenerator) return null;

		try {
			texture.mapping = EquirectangularReflectionMapping;
			const envMap = this.#pmremGenerator.fromEquirectangular(texture).texture;
			this.#preloadedEnvMaps.set(key, envMap);
			return envMap;
		} catch (e) {
			console.warn('[PmremPreloader] Error generating PMREM sync for key:', key, e);
			return null;
		}
	}

	/**
	 * Get a pre-generated environment map by key.
	 * Returns null if not yet ready.
	 */
	getPreloaded(key: string): Texture | null {
		return this.#preloadedEnvMaps.get(key) ?? null;
	}

	/**
	 * Check if a texture has been pre-generated
	 */
	isReady(key: string): boolean {
		return this.#preloadedEnvMaps.has(key);
	}

	/**
	 * Get all pre-loaded keys
	 */
	getPreloadedKeys(): string[] {
		return Array.from(this.#preloadedEnvMaps.keys());
	}

	/**
	 * Schedule queue processing during idle time
	 */
	private scheduleProcessing(): void {
		if (this.#isProcessing || this.#pendingJobs.length === 0) {
			return;
		}

		if (this.#idleCallbackId !== null) {
			return; // Already scheduled
		}

		// Use requestIdleCallback if available, otherwise setTimeout
		if (typeof requestIdleCallback !== 'undefined') {
			this.#idleCallbackId = requestIdleCallback(
				(deadline) => {
					this.#idleCallbackId = null;
					this.processNextJob(deadline);
				},
				{ timeout: 2000 } // Process within 2 seconds max
			);
		} else {
			// Fallback for browsers without requestIdleCallback
			this.#idleCallbackId = window.setTimeout(() => {
				this.#idleCallbackId = null;
				this.processNextJob(null);
			}, 100) as unknown as number;
		}
	}

	/**
	 * Process the next job in the queue
	 */
	private processNextJob(deadline: IdleDeadline | null): void {
		if (this.#pendingJobs.length === 0 || !this.#pmremGenerator) {
			this.#isProcessing = false;
			return;
		}

		// Check if we have enough idle time (need at least 50ms for PMREM generation)
		// If deadline is null (setTimeout fallback), proceed anyway
		if (deadline && deadline.timeRemaining() < 50 && !deadline.didTimeout) {
			// Not enough time, reschedule
			this.scheduleProcessing();
			return;
		}

		this.#isProcessing = true;
		const job = this.#pendingJobs.shift()!;

		try {
			job.texture.mapping = EquirectangularReflectionMapping;
			const envMap = this.#pmremGenerator.fromEquirectangular(job.texture).texture;
			this.#preloadedEnvMaps.set(job.key, envMap);
			job.resolve(envMap);
		} catch (e) {
			console.error('[PmremPreloader] Error generating PMREM for key:', job.key, e);
			job.reject(e instanceof Error ? e : new Error(String(e)));
		}

		this.#isProcessing = false;

		// Schedule next job if there are more
		if (this.#pendingJobs.length > 0) {
			this.scheduleProcessing();
		}
	}

	/**
	 * Clear a specific pre-loaded environment map
	 */
	clearPreloaded(key: string): void {
		const envMap = this.#preloadedEnvMaps.get(key);
		if (envMap) {
			try {
				envMap.dispose();
			} catch (e) {
				console.warn('[PmremPreloader] Error disposing envMap:', key, e);
			}
			this.#preloadedEnvMaps.delete(key);
		}
	}

	/**
	 * Dispose of all resources
	 */
	dispose(): void {
		// Cancel pending idle callback
		if (this.#idleCallbackId !== null) {
			if (typeof cancelIdleCallback !== 'undefined') {
				cancelIdleCallback(this.#idleCallbackId);
			} else {
				clearTimeout(this.#idleCallbackId);
			}
			this.#idleCallbackId = null;
		}

		// Reject pending jobs
		for (const job of this.#pendingJobs) {
			job.reject(new Error('PmremPreloader disposed'));
		}
		this.#pendingJobs = [];

		// Dispose pre-loaded environment maps
		for (const envMap of this.#preloadedEnvMaps.values()) {
			try {
				envMap.dispose();
			} catch (e) {
				console.warn('[PmremPreloader] Error disposing envMap:', e);
			}
		}
		this.#preloadedEnvMaps.clear();

		// Dispose PMREM generator
		if (this.#pmremGenerator) {
			this.#pmremGenerator.dispose();
			this.#pmremGenerator = null;
		}

		this.#shaderCompiled = false;
		this.#renderer = null;
	}
}
